import React, { useEffect, useRef, useState } from 'react';
import Map, { Source, Layer, NavigationControl, ScaleControl } from 'react-map-gl';
import wellknown from 'wellknown';
import { ReactComponent as LayersIcon } from '../../assets/layer-group-solid.svg';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_KEY;

const styleOptions = [
  { label: 'Street', style: 'mapbox://styles/mapbox/streets-v12' },
  { label: 'Hybrid', style: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { label: 'Satellite', style: 'mapbox://styles/mapbox/satellite-v9' },
  { label: 'Terrain', style: 'mapbox://styles/mapbox/outdoors-v12' },
  { label: 'Dark', style: 'mapbox://styles/mapbox/dark-v11' },
  { label: 'Light', style: 'mapbox://styles/mapbox/light-v11' }
];

const layerColors = [
  '#1f77b4', '#d62728', '#2ca02c', '#ff7f0e', '#9467bd',
  '#17becf', '#e377c2', '#7f7f7f', '#bcbd22', '#393b79'
];

const extractCoordinates = (geometry) => {
  const { type, coordinates } = geometry;
  switch (type) {
    case 'Point': return [coordinates];
    case 'LineString':
    case 'MultiPoint': return coordinates;
    case 'Polygon':
    case 'MultiLineString': return coordinates.flat(1);
    case 'MultiPolygon': return coordinates.flat(2);
    default: return [];
  }
};

const PreviewMapComponent = ({ data, details }) => {
  const mapRef = useRef(null);
  const [mapStyle, setMapStyle] = useState(styleOptions[0].style);
  const [mapInstance, setMapInstance] = useState(null);
  const [currentStyle, setCurrentStyle] = useState('Dark');
  const [layerVisibility, setLayerVisibility] = useState({});
  const [geoJsonLayers, setGeoJsonLayers] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    mapStyle: false,
    dataLayers: true
  });
  
  useEffect(() => {
    if (!data || !mapInstance) return;
  
    let filteredData = data;
  
    if ((details?.location_name || '').toLowerCase().includes('rbfs')) {
      const modelSoftware = (details?.model_software_name || '').toLowerCase();
      filteredData = data.filter(item => {
        const location = item?.locationName?.toLowerCase() || '';
        const hydraulicRegex = /\/hydraulic[^\/]*\//i;
        const hydrologicRegex = /\/hydrologic[^\/]*\//i; 
        const spatialRegex = /\/spatial data[^\/]*\//i;

        if (modelSoftware.includes('hms')) {
          return hydrologicRegex.test(location);
        } else if (modelSoftware.includes('ras')) {
          return hydraulicRegex.test(location);
        } else {
          return spatialRegex.test(location);
        }
      });
    }
    
    console.log('Filtered spatial data:', filteredData);

    if (filteredData.length === 0) {
      filteredData = data;  // Use the original data if no RBFS-specific data is found
    }

    const groupedFeatures = filteredData.reduce((acc, spatial_extent) => {
      if (spatial_extent.geometryWktStr) {
        const fileName = spatial_extent.fileName || 'Unnamed Layer';
        if (!acc[fileName]) {
          acc[fileName] = [];
        }
        const geojson = wellknown.parse(spatial_extent.geometryWktStr);
        acc[fileName].push({
          type: 'Feature',
          geometry: geojson,
        });
      }
      return acc;
    }, {});
    
    // ... rest of the effect continues as-is
  
    const layers = Object.entries(groupedFeatures).map(([fileName, features], index) => ({
      id: `layer-${index}`,
      fileName,
      data: {
        type: 'FeatureCollection',
        features,
      },
      color: layerColors[index % layerColors.length],
    }));

    setGeoJsonLayers(layers);

    const initialVisibility = layers.reduce((acc, layer) => {
      acc[layer.id] = true;
      return acc;
    }, {});
    setLayerVisibility(initialVisibility);

    const allCoordinates = layers.flatMap(layer =>
      layer.data.features.flatMap(feature =>
        extractCoordinates(feature.geometry)
      )
    );

    const validCoordinates = allCoordinates.filter(
      (coord) => Array.isArray(coord) && coord.length === 2 && !isNaN(coord[0]) && !isNaN(coord[1])
    );

    if (validCoordinates.length > 0) {
      const bounds = validCoordinates.reduce(
        ([minLng, minLat, maxLng, maxLat], [lng, lat]) => [
          Math.min(minLng, lng),
          Math.min(minLat, lat),
          Math.max(maxLng, lng),
          Math.max(maxLat, lat),
        ],
        [Infinity, Infinity, -Infinity, -Infinity]
      );

      mapInstance.fitBounds(bounds, { padding: 50 });
    }
  }, [data, mapInstance, details]);

  const handleStyleChange = (styleOpt) => {
    setMapStyle(styleOpt.style);
    setCurrentStyle(styleOpt.label);
  };

  const handleMapLoad = (event) => {
    setMapInstance(event.target);
  };

  const toggleLayerVisibility = (layerId) => {
    setLayerVisibility(prev => ({
      ...prev,
      [layerId]: !prev[layerId]
    }));
  };

  const selectAllLayers = () => {
    const newVisibility = {};
    geoJsonLayers.forEach(layer => {
      newVisibility[layer.id] = true;
    });
    setLayerVisibility(newVisibility);
  };

  const deselectAllLayers = () => {
    const newVisibility = {};
    geoJsonLayers.forEach(layer => {
      newVisibility[layer.id] = false;
    });
    setLayerVisibility(newVisibility);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className={`map-container`}>
      <Map
        mapLib={import('mapbox-gl')}
        ref={mapRef}
        initialViewState={{
          longitude: -99.9018,
          latitude: 31.9686,
          zoom: 6,
        }}
        style={{ height: '100%', width: '100%' }}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        onLoad={handleMapLoad}
      >
        <NavigationControl position="top-left" />
        <ScaleControl position="bottom-left" unit="imperial" />

        <div className="layer-switcher">
          <LayersIcon className="layer-icon" />
          <div className="layer-dropdown">
            <div className="layer-section">
              <div className="layer-section-header" onClick={() => toggleSection('mapStyle')}>
                <span className="layer-section-title">Map Style</span>
                <span className={`layer-section-icon ${expandedSections.mapStyle ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.mapStyle && (
                <div className="layer-section-content">
                  {styleOptions.map((option) => (
                    <div
                      key={option.label}
                      className="layer-option"
                      onClick={() => handleStyleChange(option)}
                    >
                      <input
                        type="radio"
                        name="mapStyle"
                        checked={mapStyle === option.style}
                        readOnly
                      />
                      <div className="layer-option-text">{option.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="layer-divider"></div>

            <div className="layer-section">
              <div className="layer-section-header" onClick={() => toggleSection('dataLayers')}>
                <span className="layer-section-title">Data Layers</span>
                <span className={`layer-section-icon ${expandedSections.dataLayers ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.dataLayers && (
                <div className="layer-section-content">
                  <div className="layer-actions">
                    <button className="layer-action-btn" onClick={selectAllLayers}>Select All</button>
                    <button className="layer-action-btn" onClick={deselectAllLayers}>Deselect All</button>
                  </div>
                  <div className="layer-list">
                    {geoJsonLayers.map((layer) => (
                      <div
                        key={layer.id}
                        className="layer-option"
                        onClick={() => toggleLayerVisibility(layer.id)}
                      >
                        <input
                          type="checkbox"
                          checked={layerVisibility[layer.id]}
                          readOnly
                        />
                        <span style={{ color: layer.color }}>●</span>
                        <div className="layer-option-text" title={layer.fileName}>
                          {layer.fileName}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {geoJsonLayers.map((layer) => (
          layerVisibility[layer.id] && (
            <Source key={layer.id} id={`source-${layer.id}`} type="geojson" data={layer.data}>
              <Layer
                id={`fill-${layer.id}`}
                type="fill"
                paint={{
                  'fill-color': layer.color,
                  'fill-opacity': 0.5,
                }}
              />
              <Layer
                id={`outline-${layer.id}`}
                type="line"
                paint={{
                  'line-color': layer.color,
                  'line-width': 2,
                }}
              />
            </Source>
          )
        ))}
      </Map>
    </div>
  );
};

export default PreviewMapComponent;
